// CHG-123 REQ-V123-035: 메시지 → 작업 변환 멀티스텝 오케스트레이터
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { ChatMessage } from '../../services/chatService';
import type { ProjectIssueType } from '../../hooks/useProjectIssueTypes';
import { createTodoFromMessage } from '../../hooks/useMyHome';
import MessageToTaskModal from './MessageToTaskModal';
import ProjectPickerModal from './ProjectPickerModal';
import MessageIssueTypePicker from './MessageIssueTypePicker';
import IssueCreateFromMessageModal from './IssueCreateFromMessageModal';

interface MessageToTaskFlowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: ChatMessage;
  contextProjectId?: string;
  contextProjectKey?: string;
}

type Stage = 'select' | 'project' | 'issuetype' | 'create';

interface SelectedProject {
  id: string;
  name: string;
  projectKey: string;
}

export default function MessageToTaskFlow({
  open, onOpenChange, message, contextProjectId, contextProjectKey,
}: MessageToTaskFlowProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<Stage>('select');
  const [project, setProject] = useState<SelectedProject | null>(null);
  const [issueType, setIssueType] = useState<ProjectIssueType | null>(null);
  const [creatingTodo, setCreatingTodo] = useState(false);

  // 모달이 열릴 때마다 stage·선택값 초기화 (이전 잔존 상태 방지)
  useEffect(() => {
    if (open) {
      setStage('select');
      // 프로젝트 채팅에서 진입한 경우 contextProject를 미리 세팅
      if (contextProjectId && contextProjectKey) {
        setProject({ id: contextProjectId, name: contextProjectKey, projectKey: contextProjectKey });
      } else {
        setProject(null);
      }
      setIssueType(null);
    }
  }, [open, contextProjectId, contextProjectKey]);

  const handleClose = () => {
    onOpenChange(false);
    // useEffect에서 다음 open 시 reset
  };

  // REQ-V123-030: contextProjectId 있으면 프로젝트 선택 스킵
  const handleCreateIssue = () => {
    if (contextProjectId && contextProjectKey) {
      setStage('issuetype');
    } else {
      setStage('project');
    }
  };

  // REQ-V123-040: "할 일 생성하기" — 추가 단계 없이 즉시 생성
  const handleCreateTodo = async () => {
    if (creatingTodo) return;
    setCreatingTodo(true);
    try {
      const todo = await createTodoFromMessage(message.messageId);
      // 토스트 + "마이 홈에서 보기" 액션
      toast.success(
        (t) => (
          <span className="flex items-center gap-2">
            <span>할 일이 마이 홈에 추가되었습니다</span>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                navigate(`/?focusTodo=${todo.id}`);
              }}
              className="font-semibold text-primary-600 underline"
            >
              마이 홈에서 보기
            </button>
          </span>
        ),
        { duration: 6000 },
      );
      queryClient.invalidateQueries({ queryKey: ['my-home', 'todos'] });
      queryClient.invalidateQueries({ queryKey: ['chatMessages'] });
      onOpenChange(false);
    } catch (err) {
      const errMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '할 일 생성에 실패했습니다.';
      toast.error(errMsg);
    } finally {
      setCreatingTodo(false);
    }
  };

  const handleProjectSelected = (p: SelectedProject) => {
    setProject(p);
    setStage('issuetype');
  };

  const handleIssueTypeSelected = (t: ProjectIssueType) => {
    setIssueType(t);
    setStage('create');
  };

  if (!open) return null;

  return (
    <>
      {stage === 'select' && (
        <MessageToTaskModal
          open
          onOpenChange={(v) => !v && handleClose()}
          message={message}
          contextProjectId={contextProjectId}
          contextProjectKey={contextProjectKey}
          onCreateIssue={handleCreateIssue}
          onCreateTodo={handleCreateTodo}
        />
      )}

      {stage === 'project' && (
        <ProjectPickerModal
          open
          onClose={handleClose}
          onBack={() => setStage('select')}
          onSelect={handleProjectSelected}
        />
      )}

      {stage === 'issuetype' && project && (
        <MessageIssueTypePicker
          open
          projectId={project.id}
          projectName={project.name}
          onClose={handleClose}
          onBack={() => {
            // 프로젝트 채팅 컨텍스트면 1단계로, 아니면 프로젝트 선택으로
            if (contextProjectId && contextProjectKey) {
              setStage('select');
            } else {
              setStage('project');
            }
          }}
          onSelect={handleIssueTypeSelected}
        />
      )}

      {stage === 'create' && project && issueType && (
        <IssueCreateFromMessageModal
          open
          message={message}
          projectId={project.id}
          projectKey={project.projectKey}
          issueTypeId={issueType.issueTypeId}
          issueTypeName={issueType.name}
          onClose={handleClose}
          onBack={() => setStage('issuetype')}
        />
      )}
    </>
  );
}
